import { useEffect, useState } from 'react'

import type { PendingQuestionnaire, QuestionnaireAnswerBundle } from './questionnaire'
import { Modal } from '@/ui/Modal'
import { Button } from '@/ui/Button'
import {
  buildQuestionnaireAnswerBundle,
  buildQuestionnaireDisplayText,
} from './questionnaire'

interface Props {
  questionnaire: PendingQuestionnaire
  submitting: boolean
  onSubmit: (bundle: QuestionnaireAnswerBundle, displayText: string) => Promise<boolean> | boolean
}

function questionLabel(question: PendingQuestionnaire['questions'][number], index: number): string {
  return question.header || `Step ${index + 1}`
}

function formatQuestionText(question: PendingQuestionnaire['questions'][number]): string {
  const raw = question.question.trim()
  const orderMatches = raw.match(/Order\s+\d{4,}\s+—/g) ?? []
  if (question.header !== 'Order Selection' && orderMatches.length < 2) {
    return raw
  }

  return raw
    .replace(/(\?)(\s+)(Order\s+\d{4,}\s+—)/, '$1\n\n$3')
    .replace(/\s+(Order\s+\d{4,}\s+—)/g, '\n$1')
}

export function QuestionWizardModal({
  questionnaire,
  submitting,
  onSubmit,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})

  const questionKey = questionnaire.questions.map((question) => question.id).join('|')

  useEffect(() => {
    setActiveIndex(0)
    setAnswers({})
  }, [questionKey])

  const activeQuestion = questionnaire.questions[activeIndex]
  const activeAnswer = activeQuestion ? (answers[activeQuestion.id] ?? '') : ''
  const isLastStep = activeIndex === questionnaire.questions.length - 1
  const bundle = buildQuestionnaireAnswerBundle(questionnaire, answers)
  const questionText = activeQuestion ? formatQuestionText(activeQuestion) : ''

  function setAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  async function submit() {
    if (!bundle || submitting) return
    const displayText = buildQuestionnaireDisplayText(bundle)
    await onSubmit(bundle, displayText)
  }

  if (!activeQuestion) return null

  return (
    <Modal open={Boolean(activeQuestion)} onClose={() => {}} size="full" className="mt-[10vh] rounded-2xl">
      <div className="border-b border-haze/40 px-6 py-5">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-ember" />
          <div>
            <h2 className="font-display text-xl text-cream">Consultation</h2>
            <p className="mt-1 text-sm text-mute">
              Answer all questions, then we will send them together.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {questionnaire.questions.map((question, index) => {
            const answered = Boolean((answers[question.id] ?? '').trim())
            const active = index === activeIndex
            return (
              <button
                key={question.id}
                type="button"
                onClick={() => !submitting && setActiveIndex(index)}
                disabled={submitting}
                className={[
                  'rounded-full border px-3.5 py-2 text-xs transition-all duration-300',
                  active ? 'border-ember text-cream bg-ink shadow-sm' : 'border-haze/50 text-mute hover:text-cream',
                  answered ? 'border-ember/50' : '',
                  submitting ? 'cursor-not-allowed opacity-60' : '',
                ].join(' ')}
              >
                {questionLabel(question, index)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="px-6 py-6">
        {submitting ? (
          <div className="rounded-xl border border-haze/40 bg-ink px-5 py-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-ember animate-pulse" />
                <span className="h-2 w-2 rounded-full bg-ember/70 animate-pulse [animation-delay:180ms]" />
                <span className="h-2 w-2 rounded-full bg-ember/40 animate-pulse [animation-delay:360ms]" />
              </div>
              <span className="font-display italic text-sm text-cream">Thinking</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-cream/90">
              Reviewing your answers and preparing the next step.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-5 flex items-center gap-2 text-xs text-mute">
              <span>{activeIndex + 1}</span>
              <span>/</span>
              <span>{questionnaire.questions.length}</span>
            </div>
            {activeQuestion.header && (
              <span className="text-xs font-mono uppercase tracking-wider text-ember">
                {activeQuestion.header}
              </span>
            )}
            <p className="mt-3 text-lg leading-relaxed text-cream whitespace-pre-line break-words">
              {questionText}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {activeQuestion.choices.map((choice) => {
                const selected = activeAnswer === choice.value
                return (
                  <button
                    key={choice.value}
                    type="button"
                    onClick={() => setAnswer(activeQuestion.id, choice.value)}
                    className={[
                      'rounded-full border px-5 py-2.5 text-sm transition-all duration-300',
                      selected
                        ? 'border-ember bg-ember/10 text-ember shadow-sm'
                        : 'border-haze/50 bg-ink text-cream hover:border-ember hover:text-ember',
                    ].join(' ')}
                  >
                    {choice.label}
                  </button>
                )
              })}
            </div>

            {activeQuestion.allow_free_text && (
              <div className="mt-4">
                <input
                  type="text"
                  value={activeQuestion.choices.some((choice) => choice.value === activeAnswer) ? '' : activeAnswer}
                  onChange={(event) => setAnswer(activeQuestion.id, event.target.value)}
                  placeholder="Or type your own answer..."
                  className="w-full rounded-xl border border-haze/50 bg-ink px-4 py-3 text-sm text-cream placeholder-mute/50 outline-none transition-all duration-300 focus:border-ember"
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-haze/40 px-6 py-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setActiveIndex((value) => Math.max(0, value - 1))}
          disabled={submitting || activeIndex === 0}
          className="rounded-full"
        >
          Back
        </Button>
        <Button
          size="sm"
          onClick={() => {
            if (isLastStep) {
              void submit()
              return
            }
            setActiveIndex((value) => Math.min(questionnaire.questions.length - 1, value + 1))
          }}
          disabled={submitting || !activeAnswer.trim() || (isLastStep && !bundle)}
          className="rounded-full"
        >
          {isLastStep ? 'Submit' : 'Next'}
        </Button>
      </div>
    </Modal>
  )
}
